import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import InputField from '@ingka/input-field';
import Text from '@ingka/text';
import '@ingka/inline-message/dist/style.css';
import { useEffect, useState } from 'react';
// Reuse the CatalogItemModal styles — same modal shape, same look.
import styles from './CatalogItemModal.module.css';

export interface RecipientFormData {
  email: string;
  name: string;
  active?: boolean;
}

interface Props {
  title: string;
  mode: 'create' | 'edit';
  initial?: Partial<RecipientFormData>;
  onClose: () => void;
  onSave: (data: RecipientFormData) => Promise<void>;
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function validateEmail(email: string): string | null {
  if (!email.trim()) return 'Email is required';
  if (email.length > 255) return 'Email cannot exceed 255 characters';
  if (!EMAIL_REGEX.test(email.trim())) return 'Email format is not valid';
  return null;
}

export default function RecipientModal({ title, mode, initial, onClose, onSave }: Props) {
  const [email, setEmail] = useState(initial?.email ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};

    if (mode === 'create') {
      const emailError = validateEmail(email);
      if (emailError) newErrors.email = emailError;
    }
    if (name.length > 255) newErrors.name = 'Name cannot exceed 255 characters';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    setSubmitError(null);
    try {
      await onSave({
        email: email.trim().toLowerCase(),
        name: name.trim(),
        active,
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: escape key handled globally via useEffect */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop is decorative; interactive elements inside handle keyboard */}
      <div className={styles.backdrop} onClick={onClose}>
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: click only stops backdrop-close bubbling */}
        <div
          className={styles.modal}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="recipient-modal-title"
        >
          <div className={styles.header}>
            <Text tagName="h2" textStyle="Heading.M" id="recipient-modal-title">
              {title}
            </Text>
            <button type="button" aria-label="Close" className={styles.closeBtn} onClick={onClose}>
              ×
            </button>
          </div>

          <div className={styles.body}>
            <div className={styles.field}>
              <InputField
                id="recipient-email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={saving || mode === 'edit'}
              />
              {mode === 'edit' && (
                <div className={styles.fieldHint}>
                  Email cannot be changed. Remove and re-add if needed.
                </div>
              )}
              {errors.email && <div className={styles.fieldError}>{errors.email}</div>}
            </div>

            <div className={styles.field}>
              <InputField
                id="recipient-name"
                label="Name (optional)"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
              />
              <div className={styles.fieldHint}>Shown in the admin list for readability.</div>
              {errors.name && <div className={styles.fieldError}>{errors.name}</div>}
            </div>

            {mode === 'edit' && (
              <div className={styles.field}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                    disabled={saving}
                  />
                  <span>Active</span>
                </label>
                <div className={styles.fieldHint}>
                  Uncheck to stop sending notifications to this recipient.
                </div>
              </div>
            )}

            {submitError && (
              <div className={styles.submitError}>
                <InlineMessage variant="negative" title="Save failed" body={submitError} />
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <Button type="tertiary" text="Cancel" disabled={saving} onClick={onClose} />
            <Button
              type="emphasised"
              text={saving ? 'Saving…' : 'Save'}
              disabled={saving}
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </>
  );
}